import apiClient from './api';
import type { TemplateType } from '../stores/templateStore';

// 发布请求参数
export interface PublishRequest {
  path: string;
  title: string;
  html: string;
  templateId: TemplateType;
  meta?: {
    description?: string;
    keywords?: string[];
    author?: string;
  };
}

// 发布响应
export interface PublishResponse {
  success: boolean;
  data?: {
    path: string;
    url: string;
    publishedAt: string;
  };
  error?: string;
}

// 已发布页面
export interface PublishedPageInfo {
  id: string;
  title: string;
  path: string;
  url: string;
  templateId: TemplateType;
  publishedAt: string;
  updatedAt: string;
  views?: number;
}

// 获取页面列表响应
export interface PagesListResponse {
  success: boolean;
  data?: {
    pages: PublishedPageInfo[];
    total: number;
  };
  error?: string;
}

// 发布服务
export const publishService = {
  /**
   * 发布页面
   * POST /api/v2/pages/publish
   */
  async publish(request: PublishRequest): Promise<PublishResponse> {
    const response = await apiClient.post('/pages/publish', request);
    return {
      success: true,
      data: response?.data?.data,
    };
  },

  /**
   * 获取已发布页面列表
   * GET /api/v2/pages
   */
  async getPages(): Promise<PagesListResponse> {
    const response = await apiClient.get('/pages');
    return {
      success: true,
      data: response?.data?.data,
    };
  },

  /**
   * 删除已发布页面
   * DELETE /api/v2/pages/:path
   */
  async deletePage(path: string): Promise<{ success: boolean; error?: string }> {
    await apiClient.delete(`/pages/${encodeURIComponent(path)}`);
    return { success: true };
  },

  /**
   * 更新已发布页面
   * PUT /api/v2/pages/:path
   */
  async updatePage(
    path: string,
    updates: Partial<PublishRequest>
  ): Promise<PublishResponse> {
    const response = await apiClient.put(
      `/pages/${encodeURIComponent(path)}`,
      updates
    );
    return {
      success: true,
      data: response?.data?.data,
    };
  },

  /**
   * 获取单个页面信息
   * GET /api/v2/pages/:path
   */
  async getPage(path: string): Promise<{ success: boolean; data?: PublishedPageInfo; error?: string }> {
    const response = await apiClient.get(`/pages/${encodeURIComponent(path)}`);
    return {
      success: true,
      data: response?.data?.data,
    };
  },
};

// 生成页面URL
export function generatePageUrl(path: string): string {
  const baseUrl = import.meta.env.VITE_SITE_URL || 'https://www.aigcoil.com';
  return `${baseUrl}/${path}/`;
}

// 验证路径格式
export function validatePath(path: string): { valid: boolean; error?: string } {
  // 路径不能为空
  if (!path || path.trim() === '') {
    return { valid: false, error: '路径不能为空' };
  }

  // 去除首尾斜杠
  const cleanPath = path.trim().replace(/^\/+|\/+$/g, '');

  // 路径只能包含字母、数字、连字符和下划线
  if (!/^[a-zA-Z0-9-_]+$/.test(cleanPath)) {
    return { valid: false, error: '路径只能包含字母、数字、连字符(-)和下划线(_)' };
  }

  // 路径长度限制
  if (cleanPath.length < 2) {
    return { valid: false, error: '路径长度至少为2个字符' };
  }

  if (cleanPath.length > 50) {
    return { valid: false, error: '路径长度不能超过50个字符' };
  }

  // 保留路径检查
  const reservedPaths = ['api', 'admin', 'login', 'logout', 'static', 'assets'];
  if (reservedPaths.includes(cleanPath.toLowerCase())) {
    return { valid: false, error: '该路径已被系统保留' };
  }

  return { valid: true };
}

// 生成安全的路径
export function sanitizePath(path: string): string {
  return path
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-_]/g, '-') // 替换非法字符为连字符
    .replace(/-+/g, '-') // 合并多个连字符
    .replace(/^-+|-+$/g, ''); // 去除首尾连字符
}

export default publishService;
